import { Typography } from '@mui/material'
import { useStyles } from './Eula.styles'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import {
  Box,
  FileUpload,
  FileUploadDivType,
  RadioButtonGroupMapping,
  RadioButtons,
} from '@labrav/react-components'
import React, { ChangeEvent, useCallback } from 'react'
import { EulaType } from '../../../modules/privateOffer/types'
import { KeyValue } from '../../../modules/privateOffer/privateOfferCreation/types'
import { isEmpty } from 'lodash'
import { updateAppAlert } from '@labrav/common'
import { useDispatch } from '../../../store'
import { renameFile } from './utils'

export type EulaPropsType = {
  selectedEulaFiles: File[]
  setEulaFile: (files: File[]) => void
  disableUpload: boolean
  handleChange: (data: KeyValue) => void
  eulaType: EulaType
  fileUploadHeading: string
  fileUploadSubHeading: string
  heading: string
  subHeading: string
  tooltipLabel: string
  deleteLink: (index: number, key: string) => () => void
  fileLinks?: string[]
  offerExpirationDiv?: React.ReactNode
  eulaOptions: RadioButtonGroupMapping[]
  showFileUploadForOptions: string[]
  eulaTypeKey?: string
  eulaFileKey?: string
  eulaOptionTitle?: string
  shouldShowEulaOptionSelectionRadioButtons?: boolean
  acceptedFormats?: string[]
  acceptedFilesTextMsg?: string
  updateErr?: (eulaErr: string) => void
  checkDuplicate?: boolean
  showRealName?: boolean
}

export const Eula = React.memo(
  ({
    eulaType,
    selectedEulaFiles,
    setEulaFile,
    disableUpload,
    handleChange,
    fileUploadHeading,
    fileUploadSubHeading,
    heading,
    subHeading,
    tooltipLabel,
    fileLinks,
    deleteLink,
    offerExpirationDiv,
    eulaOptions,
    showFileUploadForOptions,
    eulaTypeKey,
    eulaFileKey,
    eulaOptionTitle,
    shouldShowEulaOptionSelectionRadioButtons = true,
    acceptedFormats = ['application/pdf', 'text/plain'],
    acceptedFilesTextMsg = 'Use plain text .txt or .pdf files only. Max file size 10 Mb',
    updateErr,
    checkDuplicate,
    showRealName,
  }: EulaPropsType) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()

    const addFile = (event: ChangeEvent<HTMLInputElement>) => {
      if (
        event.target !== null &&
        event.target.files !== null &&
        event.target.files.length !== 0
      ) {
        const file = event.target.files[0]
        const newFileName = file?.name?.replaceAll(' ', '-')

        if (checkDuplicate) {
          const selectedFiles = [...selectedEulaFiles].map(
            fileItem => fileItem?.name
          )
          const existingFiles = [...(fileLinks || [])].map(
            fileItem => fileItem?.split('-file-')[1]
          )

          const isAlreadyUploaded = [
            ...selectedFiles,
            ...existingFiles,
          ].includes(newFileName)
          if (isAlreadyUploaded) {
            dispatch(
              updateAppAlert({
                message: `${newFileName} is already uploaded.`,
                messageType: 'ERROR',
                autoClose: true,
              })
            )
          } else {
            const files = [...selectedEulaFiles].concat(
              renameFile(file, newFileName)
            )
            setEulaFile(files)
          }
        } else {
          const files = [...selectedEulaFiles].concat(file)
          setEulaFile(files)
        }

        // eslint-disable-next-line @typescript-eslint/no-empty-function
        ;(event.target as HTMLInputElement).value = ''
      }
    }
    const deleteFile = useCallback(
      (index: number, key: FileUploadDivType) => async () => {
        if (key === FileUploadDivType.FILE) {
          const selFiles = [...selectedEulaFiles]
          selFiles.splice(index, 1)
          setEulaFile(selFiles)
        } else {
          deleteLink(index, eulaFileKey || 'eulaFile')()
        }
      },
      [selectedEulaFiles]
    )
    const handleUpdate = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        handleChange({
          key: eulaTypeKey || 'eulaType',
          value: (event.target as HTMLInputElement).value,
        })
      },
      [eulaType, selectedEulaFiles]
    )
    return (
      <Box
        heading={heading}
        subHeading={subHeading}
        tooltipLabel={tooltipLabel}
        icon={<InfoOutlined className={classes.infoIcon} />}
      >
        {shouldShowEulaOptionSelectionRadioButtons ? (
          <>
            <Typography className={classes.selectionHeading}>
              {eulaOptionTitle || 'Select or upload a EULA'}
            </Typography>
            <RadioButtons
              checkedValue={{ key: eulaType, value: eulaType }}
              handleChange={handleUpdate}
              radioButtonMappings={eulaOptions}
              customChild={
                <FileUpload
                  heading={fileUploadHeading}
                  subHeading={fileUploadSubHeading}
                  disableUpload={disableUpload}
                  addFile={addFile}
                  deleteFile={deleteFile}
                  selectedFiles={selectedEulaFiles}
                  fileLinks={fileLinks}
                  acceptedFormats={acceptedFormats}
                  acceptedFilesTextMsg={acceptedFilesTextMsg}
                  updateErr={updateErr}
                  showRealName={showRealName}
                />
              }
            />
          </>
        ) : (
          <></>
        )}
        {!isEmpty(offerExpirationDiv) ? offerExpirationDiv : null}
        {showFileUploadForOptions.includes(eulaType) && (
          <FileUpload
            heading={fileUploadHeading}
            subHeading={fileUploadSubHeading}
            disableUpload={disableUpload}
            addFile={addFile}
            deleteFile={deleteFile}
            selectedFiles={selectedEulaFiles}
            fileLinks={fileLinks}
            acceptedFormats={acceptedFormats}
            acceptedFilesTextMsg={acceptedFilesTextMsg}
            updateErr={updateErr}
            showRealName={showRealName}
          />
        )}
      </Box>
    )
  }
)
